import { createElement as rc, useCallback, useContext, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { contexts } from 'lib_ui-primitives';
import { metadata } from 'lib_ui-services';
import { default as _DropDown } from '../_abstractComponent/DropDown';
import useEventSink from '../../hooks/useEventSink';
import useSearchElement from './useSearchElement';
import useActiveComponentAwareness from '../../hooks/useActiveComponentAwareness';

const defaultSort = [
    {
        id: '5a624d17c1885033a496b31f',
        hNodeType: 'OrderBy',
        hNodeTypeGroup: 'filter',
        propertyName: 'title',
        direction: 'ascending'
    }
];

const BEGINNING_OF_STRING_CHAR = '^';

/**
 * @typedef {Object} Props
 * @property {Object} hNode
 * @property {string} currentRoute
 */
/** @type {import('react').FC<Props>} */
function DropDownSearch(props) {
    const {
        hNode,
        hNode: {
            title,
            foreignNamespace,
            foreignRelation,
            children = defaultSort,
            dropdownDisplayProperties,
            propertyName,
            choices
        },
        filterWrapper = x => x
    } = props || { hNode: {} };
    // Fixed choices use the dropdown's existing local records path; no relation query is needed.
    const records = useMemo(() => choices?.map(title => ({ _id: title, title })), [choices]);
    const [, publish] = useEventSink();
    const { active, onBlur, onFocus } = useActiveComponentAwareness();

    //This is specific for dropdowns, to support cascading dropdowns.
    const changeAndNotify = useCallback(
        setValue => {
            return value => {
                setValue(value);
                publish(
                    { value },
                    {
                        verb: 'select',
                        namespace: foreignNamespace,
                        relation: foreignRelation,
                        status: 'success'
                    }
                );
            };
        },
        [foreignNamespace, foreignRelation, publish]
    );

    const hasOrUndefined = children.some(c => c.hNodeType === 'OrUndefined');

    // #1977 A dropdown with `autoHide` and exactly one record in its foreign relation
    // selects that record and then removes itself from the page. On a form that is fine --
    // the value lands on the record being edited. Here it would narrow every query the
    // page makes by a criterion the user cannot see and cannot clear, which silently drops
    // rows whenever the foreign key on those rows does not match (#1985). So the control
    // still hides, and it still publishes its selection so dependent dropdowns stay
    // filtered and enabled, but it contributes nothing to the search.
    // A ref, not state: the abstract dropdown selects the record in the same tick it tells
    // us it is hiding, so a state flag would publish the criterion for one render first.
    const autoHidden = useRef(false);
    const valueChanged = useContext(contexts.SearchContext);
    const onAutoHide = useCallback(() => {
        autoHidden.current = true;
        // A value retained from an earlier mount on this route is republished before the
        // auto-hide check comes back, and when it matches the auto-selection no further
        // selection follows to overwrite it. Withdraw it here. Optional because only a
        // SearchPane provides this context; outside one there is no filter to withdraw.
        valueChanged?.(hNode, {});
    }, [valueChanged, hNode]);

    //How a selected value is translated into a filter depends on what the search element captures
    const valueToFilter = useCallback(
        value => {
            if (autoHidden.current) return {};
            if (value != null && value._id != null) {
                const basePath = metadata.getPathToProperty(hNode);
                let criteria;
                if (choices?.length) {
                    criteria = { [basePath]: { $eq: value._id } };
                } else if (value._id === 'hint-regex') {
                    const prop = (dropdownDisplayProperties || [propertyName])[0];
                    criteria = { [`${basePath}.${prop}`]: { $regex: [BEGINNING_OF_STRING_CHAR + value.value.replaceAll('*', '.*'), 'i'] } };
                } else {
                    criteria = { [`${basePath}._id`]: { $eq: value._id } };
                }
                if (hasOrUndefined) {
                    criteria = { $or: [criteria, { [basePath]: { $exists: false } }] };
                }
                return filterWrapper(criteria);
            }
            return {};
        },
        [dropdownDisplayProperties, hNode, propertyName, filterWrapper, hasOrUndefined, choices]
    );

    const { value, setValue } = useSearchElement(props, valueToFilter);

    const hNodeWithDefaultOrder = useMemo(() => ({ ...hNode, children }), [hNode, children]);

    // prettier-ignore
    return rc(_DropDown, {
        ...props,
        active,
        onBlur,
        onFocus,
        hNode: hNodeWithDefaultOrder,
        records,
        value,
        setValue: changeAndNotify(setValue),
        onAutoHide,
        title
    });
}

DropDownSearch.defaultProps = {
    disabled: false
};

DropDownSearch.propTypes = {
    hNode: PropTypes.object.isRequired,
    currentRoute: PropTypes.string.isRequired,
    disabled: PropTypes.bool
};

export default DropDownSearch;
